import { ResponsiveBar } from "@nivo/bar";
import { AlertTriangle } from "lucide-react";
import type { ReactNode } from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * Building blocks shared by the B2C Funnel tab and the RE funnel tab
 * (plan 2026-10-08 REF-141): the headline tile, the segmented button
 * group, the empty state, the horizontal stage bar and the "recent cohort
 * still converting" banner. Moved here unchanged from FunnelKpiCards and
 * FunnelAnalyticsPage; /funnel renders exactly what it rendered before.
 */

export function KpiCard({
  icon,
  label,
  value,
  sublabel,
  muted = false,
  className,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  sublabel?: string;
  muted?: boolean;
  className?: string;
}) {
  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          {icon}
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className={`text-2xl font-semibold tabular-nums ${muted ? "text-muted-foreground/60" : ""}`}
        >
          {value}
        </div>
        <div className="text-xs text-muted-foreground">{sublabel || " "}</div>
      </CardContent>
    </Card>
  );
}

export function ButtonGroup({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="flex rounded-md border">
        {options.map((opt) => (
          <Button
            key={opt.value}
            type="button"
            size="sm"
            variant={opt.value === value ? "secondary" : "ghost"}
            className="rounded-none first:rounded-l-md last:rounded-r-md"
            onClick={() => onChange(opt.value)}
          >
            {opt.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

export function EmptyState({ text }: { text: string }) {
  return (
    <div className="flex h-[200px] items-center justify-center text-sm text-muted-foreground">
      {text}
    </div>
  );
}

export type FunnelStageBarDatum = {
  /** Axis label, already suffixed by the caller when it needs a tag. */
  label: string;
  reached: number;
  /** Greyed bar: a stage from another source than the funnel's own rows. */
  muted?: boolean;
  /** Extra tooltip line (e.g. where a muted bar's number comes from). */
  note?: string;
};

/**
 * The funnel's horizontal stage bar: first stage on top, the count of
 * people who reached each stage. Muted bars are grey; the others keep the
 * funnel teal.
 */
export function FunnelStageBar({
  stages,
  ariaLabel,
}: {
  stages: readonly FunnelStageBarDatum[];
  ariaLabel?: string;
}) {
  return (
    <div
      className="h-[340px]"
      role={ariaLabel ? "img" : undefined}
      aria-label={ariaLabel}
    >
      <ResponsiveBar
        data={[...stages].reverse().map((s) => ({
          stage: s.label,
          reached: s.reached,
          pre_contact: s.muted ? 1 : 0,
          note: s.note ?? "",
        }))}
        indexBy="stage"
        keys={["reached"]}
        layout="horizontal"
        colors={({ data: d }) => (d.pre_contact ? "#9ca3af" : "#61cdbb")}
        margin={{ top: 10, right: 30, bottom: 30, left: 130 }}
        padding={0.3}
        enableGridX
        enableGridY={false}
        labelSkipWidth={20}
        axisBottom={{ tickSize: 0, tickPadding: 8 }}
        axisLeft={{ tickSize: 0, tickPadding: 8 }}
        theme={{
          text: { fill: "var(--color-muted-foreground)" },
          axis: {
            ticks: { text: { fill: "var(--color-muted-foreground)" } },
          },
          grid: { line: { stroke: "var(--color-border)" } },
        }}
        tooltip={({ value, indexValue, data: d }) => (
          <div className="rounded bg-secondary px-2 py-1 text-xs text-secondary-foreground shadow">
            <strong>{String(indexValue)}</strong>: {value}
            {d.note ? (
              <div className="text-muted-foreground">{d.note}</div>
            ) : null}
          </div>
        )}
      />
    </div>
  );
}

/** Shown while the window includes people who arrived too recently to finish. */
export function IncompleteCohortAlert({
  children = (
    <>
      This window includes contacts who just arrived and haven&apos;t had time
      to finish onboarding yet, so the later stages will keep rising. Compare
      completed cohorts for a stable conversion rate.
    </>
  ),
}: {
  children?: ReactNode;
}) {
  return (
    <Alert>
      <AlertTriangle className="size-4" />
      <AlertTitle>Recent cohort still converting</AlertTitle>
      <AlertDescription>{children}</AlertDescription>
    </Alert>
  );
}
